import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import RequestBuilder from '~/builders/http/RequestBuilder'
import {
  LoanInfoResult,
  LoanMerchantName,
  LoanTargetResult,
  PiraeusLoanInterestType,
  SantanderLoanInstallments,
  SantanderTargetResult
} from '~/models/loan'
import {
  TBI_DEFAULT_INTEREST,
  TBI_INSURANCE_PERCENTAGE
} from '~/constants/loan/tbi'
import { OnlineLoanType, SantanderTargetSchema } from '~/constants/loan/loans'

@containerScoped()
export default class LoanService {
  constructor(@inject(RequestBuilder) private requestBuilder: RequestBuilder) {}

  getLoanInfo(
    merchantName: LoanMerchantName,
    amount: number,
    installments: number,
    loanType: OnlineLoanType,
    classifiedId?: number,
    salaried?: number,
    ignorePopularity?: boolean,
    isMortgage?: boolean,
    interestType?: PiraeusLoanInterestType,
    fixedRateYears?: number
  ): Promise<LoanInfoResult> {
    const url = isMortgage
      ? `/api/loans/mortgage/${merchantName}/info/`
      : `/api/loans/${merchantName}/info/`
    return this.requestBuilder
      .request('get', url)
      .params({
        amount,
        installments,
        loan_type: loanType,
        classified: classifiedId,
        is_salaried: salaried,
        ignore_popularity: ignorePopularity,
        interest_type: interestType,
        fixed_rate_years: fixedRateYears
      })
      .send()
  }

  getSantanderLoanInfo(
    classifiedId: number,
    downpayment: number,
    installments: number,
    ignorePopularity: boolean
  ): Promise<SantanderLoanInstallments> {
    return this.requestBuilder
      .request('get', `/api/loans/classifieds/${classifiedId}/info/`)
      .params({
        downpayment,
        installments,
        ignore_popularity: ignorePopularity
      })
      .send()
  }

  getLoanTarget(
    merchantName: LoanMerchantName,
    amount: number,
    installments: number,
    loanType?: OnlineLoanType,
    classifiedId?: number
  ): Promise<LoanTargetResult> {
    return this.requestBuilder
      .request('get', `/api/loans/${merchantName}/target/`)
      .params({
        amount,
        installments,
        loan_type: loanType,
        classified: classifiedId
      })
      .send()
  }

  getSantanderLoanTarget(
    classifiedId: number,
    form: any
  ): Promise<SantanderTargetResult> {
    return this.requestBuilder
      .request('post', `/api/loans/classifieds/${classifiedId}/target/`)
      .data({
        ...form
      })
      .send()
  }

  getSantanderTargetUrlSchema(
    classifiedId: number
  ): Promise<SantanderTargetSchema> {
    return this.requestBuilder
      .request('get', `/api/loans/classifieds/${classifiedId}/target/`)
      .send()
  }

  calculateTbiInstallmentAmount(
    amount: number,
    installments: number,
    interest: number = TBI_DEFAULT_INTEREST,
    loanFeeAmount: number = 100
  ): number {
    const monthlyFee = loanFeeAmount / installments
    const interestPerMonth = interest / 100 / 12
    const appliedInterest = (1 + interestPerMonth) ** installments
    const monthlyAmount =
      amount * (interestPerMonth / (appliedInterest - 1) + interestPerMonth)
    const monthlyInsurance = (amount + loanFeeAmount) * TBI_INSURANCE_PERCENTAGE
    return monthlyAmount + monthlyFee + monthlyInsurance
  }
}
